import { collection, getDocs, query, where, orderBy, limit as fbLimit, QueryConstraint } from 'firebase/firestore';
import { db } from './firebase';
import { User, AccessScope, isFullAccess } from '../types';

// Carrega check-ins JÁ ESCOPADOS pelo servidor — espelha o `clientsQuery.ts`.
// POR QUE EXISTE: as firestore.rules pararam de deixar o GESTOR ler a coleção inteira; ele só lê
// `sellerId ∈ equipeSellerIds`. E regra do Firestore NÃO filtra: ela RECUSA a consulta inteira que
// possa retornar doc proibido. Então toda tela de staff precisa emitir a query já com o filtro —
// por lotes de 30 (limite do operador `in`), mesclando no cliente.
//   • acesso total -> 1 consulta sem filtro de vendedor;
//   • gestor       -> N consultas (lotes de `scope.sellerIds`), mescladas e deduplicadas por id;
//   • vendedor     -> 1 consulta com `sellerId == uid`.
// Espelha mobile/src/services/checkinsQuery.ts.

const chunk = <T,>(arr: T[], n: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
};

export interface CheckinsOpts {
  desde?: Date;              // createdAt >=
  ate?: Date;                // createdAt <= (ou < quando ateExclusivo)
  ateExclusivo?: boolean;
  clientId?: string;         // visitas de um cliente específico
  sellerId?: string;         // um vendedor específico (já dentro do escopo de quem consulta)
  limitTotal?: number;       // teto por consulta E do resultado final
  ordem?: 'asc' | 'desc';    // padrão desc
  /** Só passe true quando NÃO houver clientId: `clientId + orderBy` exigiria índice composto novo. */
  ordenarNoServidor?: boolean;
  /** Só as visitas que o servidor marcou como possível duplicada (ver functions: marcarVisitasDuplicadas).
   *  Consulta SEPARADA de propósito: a tela de Histórico carrega os 500 check-ins mais recentes, que
   *  com 236 vendedores é uma fração de um dia — as duas visitas de um par suspeito quase nunca
   *  caberiam juntas nessa janela. Combinado com `ordenarNoServidor`, exige índice composto; por isso
   *  quem usa esta opção ordena no cliente. */
  somenteDuplicadas?: boolean;
}

export async function fetchCheckinsEscopado(
  user: User | null,
  scope: AccessScope,
  opts: CheckinsOpts = {}
): Promise<any[]> {
  if (!user) return [];
  const col = collection(db, 'checkins');
  const ordem = opts.ordem || 'desc';

  // Filtros comuns a todos os ramos.
  const base: QueryConstraint[] = [];
  if (opts.clientId) base.push(where('clientId', '==', opts.clientId));
  if (opts.desde) base.push(where('createdAt', '>=', opts.desde));
  if (opts.ate) base.push(where('createdAt', opts.ateExclusivo ? '<' : '<=', opts.ate));
  // `checkoutAt == null` junto: a marca é informativa e pode ficar para trás numa visita que já
  // fechou (a limpeza é best-effort e o gatilho não repete). Sem este filtro, uma marca presa
  // apareceria no painel para sempre. Três igualdades sem orderBy não exigem índice composto —
  // é a mesma forma que o `consultarVisitaAberta` já roda em produção.
  if (opts.somenteDuplicadas) {
    base.push(where('possivelDuplicada', '==', true));
    base.push(where('checkoutAt', '==', null));
  }

  // orderBy/limit só quando pedido: com `clientId` isso exigiria um índice composto que não existe
  // (as telas que usam clientId ordenam no cliente, como já faziam).
  const monta = (extra: QueryConstraint[]) => {
    const cs = [...extra, ...base];
    if (opts.ordenarNoServidor) {
      cs.push(orderBy('createdAt', ordem));
      if (opts.limitTotal) cs.push(fbLimit(opts.limitTotal));
    } else if (opts.somenteDuplicadas) {
      // Teto de seguranca SEM orderBy — limit sozinho nao exige indice composto. Se um dia algo
      // marcar documentos em massa, a tela nao vira um download de milhares de docs por abertura.
      cs.push(fbLimit(opts.limitTotal || 300));
    }
    return query(col, ...cs);
  };

  const mapDocs = (snaps: any[]) => {
    const byId = new Map<string, any>();
    snaps.forEach((sn) => sn.forEach((d: any) => byId.set(d.id, { id: d.id, ...d.data() })));
    return Array.from(byId.values());
  };

  let linhas: any[];
  if (opts.sellerId) {
    // Vendedor específico já escolhido na tela: 1 consulta. O seletor só lista quem está no escopo,
    // e a regra confere de novo (`sellerId ∈ equipeSellerIds`). Vendedor só consulta a si mesmo.
    const alvo = user.role === 'seller' ? user.id : opts.sellerId;
    linhas = mapDocs([await getDocs(monta([where('sellerId', '==', alvo)]))]);
  } else if (isFullAccess(user.role) || scope.full) {
    linhas = mapDocs([await getDocs(monta([]))]);
  } else if (user.role === 'seller') {
    linhas = mapDocs([await getDocs(monta([where('sellerId', '==', user.id)]))]);
  } else {
    // GESTOR: lotes de 30 sobre o escopo. `sellerIds` vem do `equipeSellerIds` gravado pelo servidor
    // (getAccessScope prioriza) — a MESMA lista que a regra valida, senão a consulta seria recusada.
    const ids = (scope.sellerIds || []).filter(Boolean);
    if (!ids.length) return [];
    // allSettled e não all: com `Promise.all`, UM lote recusado (um uid que saiu da equipe entre o
    // cálculo do escopo e a consulta) rejeitava tudo e a tela ficava vazia. Agora aproveitamos os
    // lotes que passaram — mas se TODOS falharem, propaga o erro para a tela poder explicá-lo
    // (senão "sem permissão" viraria "nenhum check-in encontrado", que é mentira).
    const res = await Promise.allSettled(chunk(ids, 30).map((c) => getDocs(monta([where('sellerId', 'in', c)]))));
    const ok = res.filter((r): r is PromiseFulfilledResult<any> => r.status === 'fulfilled');
    if (!ok.length) throw (res[0] as PromiseRejectedResult).reason;
    linhas = mapDocs(ok.map((r) => r.value));
  }

  // Ordena/corta no cliente: com lotes, cada consulta traz seu próprio "top N" — o corte final tem
  // que ser feito depois da mesclagem, senão o resultado fica enviesado por lote.
  const t = (v: any) => (v?.toDate ? v.toDate().getTime() : v instanceof Date ? v.getTime() : 0);
  linhas.sort((a, b) => (ordem === 'asc' ? t(a.createdAt) - t(b.createdAt) : t(b.createdAt) - t(a.createdAt)));
  return opts.limitTotal ? linhas.slice(0, opts.limitTotal) : linhas;
}
